<template>
  <li
    class="grid grid-cols-[min-content_1fr_min-content] py-4 mx-4 border-b border-b-gray-200 gap-x-1 items-start"
  >
    <div class="row-span-2 self-start h-full py-0.5">
      <CheckCircleIcon
        v-if="job?.status === status.done"
        class="size-4 text-green-500"
        aria-hidden="true"
      />

      <ExclamationCircleIcon
        v-if="job?.status === status.failed"
        class="size-4 text-red-600"
        aria-hidden="true"
      />
    </div>

    <p
      class="truncate text-sm font-medium text-gray-900"
      :title="CODE_LABELS?.[job?.code] ?? job?.code"
    >
      {{ CODE_LABELS?.[job?.code] ?? job?.code }}
    </p>
    <div class="row-span-2 flex flex-col text-xs whitespace-nowrap items-end">
      <LoaderCircle
        v-if="job?.status === status.pending"
        class="size-6 animate-spin fill-blue-600 text-gray-200 self-center"
      />
      <span
        v-if="job?.status === status.done"
        class="text-green-500 font-semibold text-xs"
        >{{ t("common.components.backgroundProcess.done") }}</span
      >
      <span
        v-if="job?.status === status.failed"
        class="text-red-600 font-semibold text-xs"
      >
        {{ t("common.components.backgroundProcess.failed") }}
      </span>
      <p v-if="job?.status === status.pending">
        {{ formatSecondsToHumanReadable(job?.started_at, new Date()) }}
      </p>
      <button
        v-if="job?.status === status.done && !isEmpty(job?.path)"
        class="btn-additional"
        @click.stop="handleDownload(job.path)"
      >
        <ArrowDownTrayIcon />
        {{ t("common.components.backgroundProcess.download") }}
      </button>
    </div>
    <p class="truncate text-xs text-gray-900 font-normal">
      <span class="text-gray-500">{{
        t("common.components.backgroundProcess.requested-on")
      }}</span>
      {{ formatDate(job?.started_at) }}
    </p>
  </li>
</template>

<script lang="ts" setup>
import { STRING_PLACEHOLDER } from "@/common/helpers/constants";
import { CODE_LABELS, status } from ".";
import isValid from "date-fns/isValid";
import { format } from "date-fns-tz";
import { intervalToDuration } from "date-fns";
import isEmpty from "lodash-es/isEmpty";
import { ArrowDownTrayIcon } from "@heroicons/vue/20/solid";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/vue/24/outline";
import LoaderCircle from "@/common/components/LoaderCircle.vue";
import { useI18n } from "vue-i18n";

defineProps<{
  job: Auth.JobUI;
}>();

const { t } = useI18n();

function formatDate(date: Date): string {
  if (isValid(date)) {
    return format(date, "yyyy-MM-dd HH:mm 'h'");
  }
  return STRING_PLACEHOLDER;
}

function formatSecondsToHumanReadable(start: Date, end: Date): string {
  if (isValid(start) && isValid(end)) {
    const duration = intervalToDuration({ start, end });
    const pad = (n: number = 0) => n.toString().padStart(2, "0");
    const parts: string[] = [];
    if (duration.days) {
      parts.push(`${duration.days}d`);
    }
    const time = `${pad(duration.hours)}:${pad(duration.minutes)}:${pad(duration.seconds)}`;
    parts.push(time);
    return parts.join(" ");
  } else {
    return STRING_PLACEHOLDER;
  }
}

function handleDownload(url: string) {
  const filename = url.split("/").pop() || "download";
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.target = "_blank";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
</script>
